/**
 * Auth API Client (cookie-based).
 *
 * The frontend used to talk directly to Cognito and stash access /
 * refresh tokens in localStorage. That made any same-origin XSS into
 * a one-line account takeover (`JSON.parse(localStorage.getItem('fias_auth'))`).
 * This file now delegates every auth operation to our own backend at
 * `/v1/auth/*`, which sets httpOnly Secure cookies that JavaScript can
 * never read. Tokens never enter JS storage of any kind.
 *
 * Other callers of this module did not need to change — the public
 * methods (signIn / signUp / signOut / getCurrentUser / etc.) keep the
 * same shapes; only the implementations switched from "POST to Cognito,
 * write to localStorage" to "POST to our backend, let it set cookies".
 *
 * Two methods hand a string credential back to callers:
 *
 * - `getWebSocketTicket()` — mints a single-use 30s DynamoDB ticket via
 *   `POST /v1/auth/ws-ticket`. WebSocket / SSE handshakes can't carry
 *   cross-origin cookies reliably, so the ticket is what we put on the
 *   `?ticket=` query param. Single-use means a leaked URL is useless
 *   after the first handshake completes.
 *
 * - `getPluginBridgeToken(archeId)` — mints a capability JWT scoped to a
 *   single arche (audience=plugin-bridge, 5min TTL). The only credential
 *   the /v1/plugins/bridge/:archId route accepts; rejected at every
 *   other authenticated route. A leaked token grants ≤5min of access to
 *   one arche's bridge handler, never full-account authority. Cached
 *   per-arche in memory (with a 30s refresh margin); concurrent callers
 *   share one in-flight mint, and the plugin renderer starts that mint on
 *   mount via `prefetchPluginBridgeToken(archeId)`. Invalidated via
 *   `invalidatePluginBridgeToken(archeId)` / wholesale on logout by
 *   `markSessionInactive()`.
 */

import { API_BASE } from './api';
import { setCsrfToken, attachCsrfHeader } from './csrf';
import { sessionCredentials } from './embed-document';
import { getCapturedAdClick, type CapturedAdClick } from './attribution';
import { ADOPT_GCLID_PATTERN } from './adopt-landing-params';
import {
  refreshAuthSession,
  markSessionEstablished,
  clearSessionEstablished,
  markSessionHint,
  clearSessionHint,
} from './auth-refresh';
import {
  isSessionRefreshingError,
  retryWhileSessionRefreshing,
  sessionRefreshingDelayMs,
} from './session-refreshing';

// =============================================================================
// Guest Trial Mode — the mint request body
// =============================================================================

/** What `POST /auth/guest-session` accepts: at most one ad click. */
export type GuestSessionBody =
  | Record<string, never>
  | { fclid: string }
  | { google: { gclid: string; campaignRef: string; creativeRef?: string } };

/**
 * The mint body for a captured ad click. A Google click is only presentable
 * WITH its campaign id (`adopt_cid`) — that is what names the campaign the
 * trial is charged to — and with a gclid the server accepts
 * (`ADOPT_GCLID_PATTERN`). Otherwise it is sent as no click at all, which the
 * strict gate refuses. That is the correct answer, not a bug to paper over:
 * the server cannot attribute it.
 */
export function guestSessionBody(click: CapturedAdClick | null): GuestSessionBody {
  if (click?.kind === 'fias') return { fclid: click.fclid };
  if (click?.kind === 'google' && click.campaignRef && ADOPT_GCLID_PATTERN.test(click.gclid)) {
    return {
      google: {
        gclid: click.gclid,
        campaignRef: click.campaignRef,
        ...(click.creativeRef ? { creativeRef: click.creativeRef } : {}),
      },
    };
  }
  return {};
}

// =============================================================================
// Dev mode
// =============================================================================
//
// VITE_DEV_MODE is honored only on hostnames that unambiguously identify a
// developer environment so a leaked build flag in staging/prod can never
// enable dev login. The backend pairs this with its own dev-mode gate
// (Cognito not configured + explicit opt-in), so even if the gate fails
// open here, the backend rejects dev tokens in any real env.
//
// Allowed hostnames:
//   - localhost / 127.0.0.1 (local WSL, Docker, native dev)
//   - *.app.github.dev      (GitHub Codespaces forwarded URLs)

const isDevHost =
  typeof window !== 'undefined' &&
  (window.location.hostname === 'localhost' ||
    window.location.hostname === '127.0.0.1' ||
    window.location.hostname.endsWith('.app.github.dev'));

if (import.meta.env.VITE_DEV_MODE === 'true' && !isDevHost) {
  console.error(
    '[Auth] SECURITY: VITE_DEV_MODE is set but hostname is not a recognized dev host. ' +
      'DEV_MODE has been disabled. This should never happen in production builds.',
  );
}

const DEV_MODE = import.meta.env.VITE_DEV_MODE === 'true' && isDevHost;

const DEV_USER_STORAGE_KEY = 'fias_dev_user_index';

/**
 * Role assignments for dev users (mirrors the backend's
 * services/jwt-verifier.ts). Used purely so the dev-user picker UI can
 * preview which roles a given dev user holds — the source of truth at
 * runtime is the backend's `getDevUserRoles`.
 */
function getDevUserRoles(num: number): string[] {
  switch (num) {
    case 1:
      return ['user', 'admin', 'contributor', 'beta_tester', 'litcraft_admin'];
    case 2:
      return ['user', 'contributor', 'beta_tester'];
    case 3:
      return ['user', 'beta_tester'];
    case 4:
      return ['user', 'contributor'];
    default:
      return ['user'];
  }
}

const DEV_USERS: AuthUser[] = Array.from({ length: 10 }, (_, i) => ({
  userId: `dev-user-${String(i + 1).padStart(3, '0')}`,
  username: `devuser${i + 1}`,
  email: i === 0 ? 'dev@localhost' : `dev${i + 1}@localhost`,
  roles: getDevUserRoles(i + 1),
  emailVerified: true,
}));

function getSelectedDevUserIndex(): number {
  if (!DEV_MODE) return 0;
  try {
    const stored = localStorage.getItem(DEV_USER_STORAGE_KEY);
    if (stored !== null) {
      const index = parseInt(stored, 10);
      if (index >= 0 && index < DEV_USERS.length) return index;
    }
  } catch {
    // Ignore storage errors
  }
  return 0;
}

function setSelectedDevUserIndex(index: number): void {
  localStorage.setItem(DEV_USER_STORAGE_KEY, String(index));
}

// =============================================================================
// Public types (shape preserved across the migration)
// =============================================================================

export interface AuthUser {
  userId: string;
  username: string;
  email: string;
  roles: string[];
  emailVerified: boolean;
  /** Guest Trial Mode fields — present only for guest sessions (roles:['guest']).
   * `expiresAt` is the absolute trial expiry (ISO); the client trial banner reads
   * it. Both survive a refresh via /auth/me. */
  displayName?: string;
  accountType?: 'guest' | 'full';
  expiresAt?: string;
  /**
   * UX-only hint from the `mfa_enrolled` ID-token claim (surfaced on
   * `/auth/me`). NOT an authorization signal — the privileged gate keys off
   * `mfa_state_version`, never this. Legacy tokens without the claim ⇒ false.
   */
  mfaEnrolled?: boolean;
  /**
   * Privileged-MFA obligation for the app-wide banner. Present only when the
   * account holds a role that requires MFA under an active gate; absent
   * otherwise (gate off, or a non-privileged account). Enforcement is
   * server-side — this only drives the banner/countdown.
   */
  privilegedMfa?: {
    mode: 'grace' | 'enforced';
    required: boolean;
    enrolled: boolean;
    deadline: string | null;
    daysRemaining: number | null;
  };
}

/**
 * Result of `signIn`. A password-correct login for an MFA-enrolled user does
 * NOT establish a session — the backend returns `{ mfaRequired: true }` and
 * sets an httpOnly challenge cookie, and the caller must complete
 * `mfaChallenge(code)` (or `mfaRecoveryLogin(...)`) to obtain cookies.
 */
export type SignInResult = { status: 'authenticated'; user: AuthUser } | { status: 'mfa_required' };

/**
 * Result of `mfaRecoveryLogin`. A recovery code spends ITSELF and nothing else:
 * two-factor stays ON, the authenticator secret is unchanged, and the remaining
 * codes still work. The user is simply signed in.
 *
 * (It used to disable MFA and return `recoveryPending`. It no longer does — the
 * server keeps the factor, records an advisory marker, and Account Settings
 * offers an OPT-IN "lost your authenticator? re-enroll" prompt.)
 */
export type RecoveryLoginResult = {
  status: 'authenticated';
  user: AuthUser;
  /** Codes left after this sign-in; absent if the server could not count them. */
  recoveryCodesRemaining?: number;
};

// -----------------------------------------------------------------------------
// MFA settings surface (authenticated).
//
// These live here — NOT in api.ts — on purpose: api.ts traces every request and
// response body into the telemetry apiTraces buffer (see lib/telemetry.ts;
// sanitizeForLogging only truncates by size, it does NOT redact by key). Routing
// MFA through it would persist the TOTP secret, recovery codes, OTP codes, and
// password verbatim in a buffer surfaced by window.fiasDebug.getState(). authFetch
// does no such tracing, keeping codes/secrets out of frontend telemetry entirely
// (a hard requirement of the MFA design).
// -----------------------------------------------------------------------------

/** The user's MFA lifecycle state. Only `enabled` satisfies the privileged gate. */
export type MfaState =
  | 'disabled'
  | 'enabling'
  | 'enabled'
  | 'disabling'
  | 'recovery_pending'
  | 'resetting';

export interface MfaStatusResponse {
  state: MfaState;
  /** True when live Cognito TOTP config disagrees with the recorded state. */
  drift: boolean;
  mfaEnrolled: boolean;
  recoveryCodesRemaining: number;
  recoveryCodesAcknowledged: boolean;
  /**
   * ISO timestamp of the last recovery-code sign-in, present only while the
   * user has neither re-enrolled nor dismissed the prompt. Advisory: MFA is
   * fully enabled regardless. Drives the "signed in with a backup code — lost
   * access to your authenticator?" prompt.
   */
  recoveryCodeUsedAt?: string;
  /**
   * Whether NEW enrollments are currently permitted. When false, the setup
   * action is disabled (the server would 403 MFA_ENROLLMENT_DISABLED). Optional
   * for backward-compat with older API responses; treated as enabled if absent
   * so an already-enrolled user is never blocked from managing MFA.
   */
  enrollmentEnabled?: boolean;
  /**
   * Privileged-enforcement gate — present ONLY for privileged roles. Drives the
   * grace-deadline countdown / enforced-block banner. `mode: 'off'` ⇒ no banner.
   */
  privilegedMfa?: {
    mode: 'off' | 'grace' | 'enforced';
    enforcementDeadline: string | null;
  };
}

/**
 * The second factor for a step-up: EITHER a live authenticator code OR a backup
 * code. Both are accepted because a TOTP-only gate traps precisely the user
 * these actions exist for — the one whose authenticator is gone. The server
 * validates a backup code without consuming it, since every step-up call site
 * replaces or retires the code set immediately afterwards.
 */
export type MfaStepUpFactor = { totpCode: string } | { recoveryCode: string };

function stepUpBody(currentPassword: string, factor: MfaStepUpFactor) {
  return { currentPassword, ...factor };
}

/** Result of `POST /mfa/setup` — the QR / deep-link / manual-entry material. */
export interface MfaSetupResponse {
  secretCode: string;
  /**
   * `otpauth://totp/…` Key URI. Drives BOTH the QR code and the mobile
   * "add to your authenticator app" deep link — same string, two transports.
   */
  otpauthUri: string;
  /**
   * The account name this enrollment will appear under in the authenticator
   * app (`bill@example.com (2026-07-28 A1B2)`). Unique per enrollment, so the
   * wizard can name the exact entry the user should be looking at. Absent on
   * older API responses ⇒ the wizard omits the "look for this entry" hint.
   */
  accountLabel?: string;
  /** Display name the entry groups under in the app. Defaults to `Fias`. */
  issuer?: string;
  /**
   * `reenroll` when MFA was already on and this is a secret ROTATION (the
   * lost-authenticator path). The wizard must then tell the user to REPLACE
   * their existing entry: the app will hold both, and only the new one works.
   * Absent on older API responses ⇒ treat as `enroll`.
   */
  mode?: 'enroll' | 'reenroll';
}

/**
 * Result of `POST /mfa/verify-setup`. Enrollment KEEPS this session alive (the
 * backend re-mints it with the new `mfa_state_version` instead of signing out),
 * so `reauthRequired` is `false` on the normal path — the caller must NOT force a
 * re-login, which would unmount the page before the one-time codes are saved.
 * `alreadyEnrolled` marks a concurrent commit that already landed `enabled`.
 */
export interface MfaVerifySetupResponse {
  recoveryCodes: string[];
  /** ISO timestamp the set was issued — printed on the offline copy. */
  generatedAt?: string;
  reauthRequired?: boolean;
  alreadyEnrolled?: boolean;
}

/** The `action` union returned by `POST /mfa/repair` (reconciliation outcome). */
export type MfaRepairAction =
  | 'none'
  | 'reported_drift'
  | 'committed_enabled'
  | 'expired_enrollment'
  | 'recorded_recovery_pending'
  | 'operator_attention'
  | 'raced';

export interface MfaRepairResponse {
  action: MfaRepairAction;
  state: MfaState;
  /** Only ever true when `action === 'committed_enabled'`. */
  reauthRequired?: boolean;
}

interface ApiErrorBody {
  error?: { code?: string; message?: string; retryAt?: string };
}

/**
 * Thrown by `authFetch` on a non-2xx response. Carries the HTTP status
 * so callers can distinguish auth failures (401) from service failures
 * (5xx) — important because any non-OK response on `/auth/ws-ticket`
 * used to be funneled through the refresh-and-retry path, which then
 * surfaced unrelated outages as "session expired" to the user.
 */
class AuthFetchError extends Error {
  readonly status: number;
  readonly code: string | undefined;
  /**
   * ISO instant at which the caller may retry. Present on 429s that carry it,
   * and on a Company portal's 503 SESSION_REFRESHING (from its Retry-After).
   * An absolute instant rather than a duration so a countdown cannot drift
   * with a slow request.
   */
  readonly retryAt: string | undefined;
  constructor(message: string, status: number, code: string | undefined, retryAt?: string) {
    super(message);
    this.name = 'AuthFetchError';
    this.status = status;
    this.code = code;
    this.retryAt = retryAt;
  }
}

/**
 * Reads the HTTP status off an error thrown by `authFetch` (undefined for a
 * network failure or any other error), so a caller can tell a refusal (4xx)
 * from an outage without depending on message text.
 */
export function getAuthErrorStatus(err: unknown): number | undefined {
  return err instanceof AuthFetchError ? err.status : undefined;
}

/** Reads the retry instant off an error thrown by `authFetch`, if it has one. */
export function getRetryAt(err: unknown): string | undefined {
  return err instanceof AuthFetchError ? err.retryAt : undefined;
}

/**
 * In-memory "we have a session" flag. Several streaming hooks call
 * `getIdToken()` synchronously as a gate to decide whether to attempt
 * a WebSocket connection at all (the actual ticket used for the
 * handshake is fetched async via `getWebSocketTicket`). Under cookie
 * auth we can't read the session token from JS, so we track presence
 * here and return a stable non-null marker when set. The marker is
 * never sent over the wire — it's purely a falsy/truthy signal.
 */
const SESSION_PRESENT_MARKER = 'fias-cookie-session';
let sessionPresent = false;

// In-memory cache for plugin bridge capability tokens.
//
// The server issues a capability JWT scoped to a single archeId with a
// 5-minute TTL. We cache per-arche so distinct plugins don't share a
// token (they can't — each is scoped to its own arche). The cache
// expires the entry 30s before the server's TTL so the in-flight
// bridge call doesn't race the expiry.
//
// Invalidated wholesale on logout via `markSessionInactive` so a
// freshly-logged-out plugin doesn't get its old token back.
const PLUGIN_BRIDGE_REFRESH_MARGIN_MS = 30 * 1000;
const cachedBridgeTokens = new Map<string, { token: string; expiresAt: number }>();
// Mints in flight, keyed like the cache. A plugin typically fires several
// bridge calls within milliseconds of booting; without this each one started
// its own mint and the answers came back one after another (~0.22s apart on
// staging), so the fifth call waited over a second. Concurrent callers share
// one request instead. The entry is dropped when the mint settles, and a mint
// whose entry was dropped early (invalidate / logout) never writes the cache —
// otherwise a token minted before logout could be served after it.
//
// `ticket` is the mint's identity: a settled mint may write the cache only
// while its ticket is still the one in the map. `quiet` stays true while only
// a prefetch is waiting, so a mint nobody asked for yet fails silently — the
// first real caller to join flips it and gets the failure logged.
interface BridgeTokenMintTicket {
  quiet: boolean;
}
const inFlightBridgeTokens = new Map<
  string,
  { ticket: BridgeTokenMintTicket; promise: Promise<string | null> }
>();

/** Bridge-token channel. Absent = production; `'test'` = a Forge test build. */
export type PluginBridgeTokenChannel = 'test';

function bridgeTokenCacheKey(archeId: string, channel?: PluginBridgeTokenChannel): string {
  return channel ? `${archeId}:${channel}` : archeId;
}

function markSessionActive(): void {
  sessionPresent = true;
  markSessionEstablished();
  // Persist a reload-durable hint so the next cold boot knows to attempt
  // refresh-and-retry (see getCurrentUser). Fires on login and every
  // confirmed /auth/me success.
  markSessionHint();
}

function markSessionInactive(): void {
  sessionPresent = false;
  cachedBridgeTokens.clear();
  inFlightBridgeTokens.clear();
  clearSessionEstablished();
  clearSessionHint();
}

// =============================================================================
// Fetch wrapper
// =============================================================================

/**
 * Performs an auth-API request with cookies attached and the CSRF
 * token (if known) echoed in the X-CSRF-Token header. Throws an Error
 * with the user-facing message from the backend's `error.message` on
 * non-2xx responses.
 */
async function authFetch<T>(
  path: string,
  init: {
    method?: string;
    body?: Record<string, unknown>;
    /**
     * How many times to ask again when a Company portal answers 503
     * SESSION_REFRESHING (the session is live and mid-refresh on the server;
     * ADR-024), each wait the server's Retry-After. Default none: only the
     * session probe is worth holding for.
     */
    sessionRefreshingRetries?: number;
  } = {},
): Promise<T> {
  const method = init.method ?? 'GET';
  const headers: Record<string, string> = {};
  if (init.body !== undefined) {
    headers['Content-Type'] = 'application/json';
  }
  attachCsrfHeader(method, headers);

  const response = await retryWhileSessionRefreshing(
    () =>
      fetch(`${API_BASE}${path}`, {
        method,
        headers,
        credentials: sessionCredentials(),
        body: init.body !== undefined ? JSON.stringify(init.body) : undefined,
      }),
    { maxRetries: init.sessionRefreshingRetries ?? 0 },
  );

  // Capture CSRF token issued by login / refresh / dev-login responses
  // so subsequent state-changing requests can include it in the header.
  if (response.ok) {
    const data: unknown = await response.json().catch(() => ({}));
    const body = data as { csrfToken?: string } & Record<string, unknown>;
    if (typeof body.csrfToken === 'string') {
      setCsrfToken(body.csrfToken);
    }
    return body as T;
  }

  let message = response.statusText;
  let code: string | undefined;
  let retryAt: string | undefined;
  try {
    const body = (await response.json()) as ApiErrorBody;
    if (body.error?.message) message = body.error.message;
    if (body.error?.code) code = body.error.code;
    // Throttle responses carry a real unlock instant so the UI can count down
    // instead of saying "try again later". Dropping it here is why the login
    // page could only render a static message.
    if (typeof body.error?.retryAt === 'string') retryAt = body.error.retryAt;
  } catch {
    // Non-JSON error body — fall back to statusText
  }
  // A portal session still refreshing when the waiting above ran out: carry
  // the server's Retry-After, so whoever asks again paces itself by it.
  if (response.status === 503 && code === 'SESSION_REFRESHING') {
    retryAt = new Date(Date.now() + sessionRefreshingDelayMs(response)).toISOString();
  }
  throw new AuthFetchError(message, response.status, code, retryAt);
}

// =============================================================================
// Auth API
// =============================================================================

class AuthApiClient {
  // -------------------------------------------------------------------------
  // Sign-up
  // -------------------------------------------------------------------------

  /**
   * An empty or absent `inviteCode` is OMITTED from the body (the route rejects
   * an empty string). Only a guest whose trial came from an ad click can sign
   * up without one — the server lifts the guest cookie as the claim and the
   * Cognito PreSignUp trigger decides.
   */
  async signUp(
    email: string,
    password: string,
    inviteCode: string | undefined,
    username: string,
  ): Promise<{ requiresConfirmation: boolean }> {
    return authFetch<{ requiresConfirmation: boolean }>('/auth/signup', {
      method: 'POST',
      body: {
        email: email.toLowerCase(),
        password,
        ...(inviteCode ? { inviteCode } : {}),
        username,
      },
    });
  }

  async confirmSignUp(
    email: string,
    code: string,
    inviteCode?: string,
    username?: string,
  ): Promise<void> {
    await authFetch('/auth/confirm-signup', {
      method: 'POST',
      body: {
        email: email.toLowerCase(),
        code,
        ...(inviteCode ? { inviteCode } : {}),
        ...(username ? { username } : {}),
      },
    });
  }

  async resendConfirmationCode(email: string): Promise<void> {
    await authFetch('/auth/resend-confirmation', {
      method: 'POST',
      body: { email: email.toLowerCase() },
    });
  }

  // -------------------------------------------------------------------------
  // Sign-in / sign-out
  // -------------------------------------------------------------------------

  async signIn(email: string, password: string): Promise<SignInResult> {
    if (DEV_MODE) {
      const devIndex = getSelectedDevUserIndex();
      console.warn(`[DEV_MODE] Bypassing Cognito — using ${DEV_USERS[devIndex].email}`);
      await authFetch('/auth/dev-login', {
        method: 'POST',
        body: { devUserIndex: devIndex + 1 },
      });
      markSessionActive();
      return { status: 'authenticated', user: DEV_USERS[devIndex] };
    }

    const result = await authFetch<{ mfaRequired?: boolean }>('/auth/login', {
      method: 'POST',
      body: { email: email.toLowerCase(), password },
    });

    // MFA-enrolled user: the password was correct but NO session cookie was
    // set. The backend stashed an encrypted `fias_mfa_challenge` cookie
    // (httpOnly, scoped to /v1/auth/mfa) carrying the email + Cognito session.
    // The caller must answer the TOTP challenge via `mfaChallenge(code)`.
    if (result.mfaRequired) {
      return { status: 'mfa_required' };
    }

    markSessionActive();
    // The cookie is set by the login response. Hydrate the user
    // profile via /auth/me so callers get back the same shape they
    // used to receive from the Cognito ID-token decode.
    const me = await this.getCurrentUser();
    if (!me) {
      throw new Error('Login succeeded but /auth/me returned no user');
    }
    return { status: 'authenticated', user: me };
  }

  /**
   * Answer a TOTP challenge issued by `signIn` when it returned
   * `mfa_required`. Reads the httpOnly `fias_mfa_challenge` cookie server-side
   * (email + Cognito session come ONLY from that cookie — never the body), so
   * this takes just the 6-digit code. On success the backend sets the auth
   * cookies and returns a fresh CSRF token (captured by `authFetch`).
   *
   * Throws `AuthFetchError` on failure: `MFA_CODE_MISMATCH` (400, retry — the
   * challenge cookie is kept) or `MFA_CHALLENGE_EXPIRED` (401, the cookie was
   * cleared and the caller must return to the password step).
   */
  async mfaChallenge(code: string): Promise<{ user: AuthUser }> {
    await authFetch('/auth/mfa/challenge', {
      method: 'POST',
      body: { code },
    });
    markSessionActive();
    const me = await this.getCurrentUser();
    if (!me) {
      throw new Error('MFA challenge succeeded but /auth/me returned no user');
    }
    return { user: me };
  }

  /**
   * Guest Trial Mode: mint an anonymous trial session. Sets the fias_session
   * cookie (server-side) and returns the guest user (roles:['guest'], with
   * accountType/expiresAt). Unlike signIn, the mint response already carries the
   * full user, so no /auth/me round-trip is needed. authFetch captures the CSRF
   * token from the response automatically.
   */
  async createGuestSession(): Promise<{ user: AuthUser }> {
    // Present the captured ad click so the trial is attributed to (and, under
    // the strict gate, permitted by) the campaign that drove the click. Always
    // send an object body (even when empty) — the route validates the body, and
    // a missing body would fail that validation.
    const result = await authFetch<{ user: AuthUser }>('/auth/guest-session', {
      method: 'POST',
      body: guestSessionBody(getCapturedAdClick()),
    });
    markSessionActive();
    return { user: result.user };
  }

  /**
   * Guest Trial Mode: finalize the Postgres side of a guest → full upgrade after
   * the first post-upgrade login (clears user_credits.is_guest and grants the
   * welcome bonus). The DynamoDB identity upgrade already happened in
   * PostConfirmation. Idempotent server-side — safe to retry, and a no-op for a
   * normal account. Requires a full-account session.
   */
  async completeGuestUpgrade(): Promise<{ finalized: boolean; alreadyFinalized: boolean }> {
    return authFetch<{ finalized: boolean; alreadyFinalized: boolean }>(
      '/auth/complete-guest-upgrade',
      { method: 'POST' },
    );
  }

  /**
   * Sign in with a recovery code when the authenticator is unavailable.
   * Consumes exactly ONE code and changes nothing else — two-factor stays on,
   * the authenticator secret is untouched, the other codes still work. `opId`
   * is a client-generated UUID idempotency key so a retry can't burn two codes.
   *
   * Every credential/code failure returns a uniform 401 `INVALID_RECOVERY`
   * (never distinguishes which field was wrong). A mid-flight failure returns
   * 503 `MFA_RECOVERY_INCOMPLETE` and is retryable with the SAME `opId` — no
   * code is spent until a session has actually been minted.
   */
  async mfaRecoveryLogin(
    email: string,
    password: string,
    recoveryCode: string,
    opId: string,
  ): Promise<RecoveryLoginResult> {
    const result = await authFetch<{ recoveryCodesRemaining?: number }>(
      '/auth/mfa/recovery-login',
      {
        method: 'POST',
        body: { email: email.toLowerCase(), password, recoveryCode, opId },
      },
    );

    // Auth cookies + CSRF are set; the account is fully intact.
    markSessionActive();
    const me = await this.getCurrentUser();
    if (!me) {
      throw new Error('Recovery login succeeded but /auth/me returned no user');
    }
    return {
      status: 'authenticated',
      user: me,
      recoveryCodesRemaining: result.recoveryCodesRemaining,
    };
  }

  /**
   * Dismiss the "you signed in with a backup code" prompt. Advisory only — it
   * authorizes nothing, so there is no step-up.
   */
  async mfaDismissRecoveryNotice(): Promise<{ success: boolean }> {
    return authFetch<{ success: boolean }>('/auth/mfa/recovery-notice/dismiss', { method: 'POST' });
  }

  // -------------------------------------------------------------------------
  // MFA settings (authenticated). Untraced by design — see the note on the MFA
  // types above. The Account Settings card drives these.
  // -------------------------------------------------------------------------

  /** Read-only MFA status. Degrades gracefully server-side (never surfaces drift as an error). */
  async getMfaStatus(): Promise<MfaStatusResponse> {
    return authFetch<MfaStatusResponse>('/auth/mfa/status');
  }

  /**
   * Begin enrollment. Proves the password, returns the TOTP secret + otpauth
   * URI, and stashes an encrypted association-session cookie (10 min) that
   * `mfaVerifySetup` binds against.
   *
   * `proof` is required ONLY when MFA is already on (a re-enrollment, i.e.
   * rotating the live factor) and must carry exactly one of `totpCode` /
   * `recoveryCode`. The server rejects a re-enrollment without it: taking over
   * an account's second factor may never rest on a session plus a password
   * alone. Omit it for a first-time enrollment, which has no factor to prove.
   */
  async mfaSetup(
    currentPassword: string,
    proof?: { totpCode?: string; recoveryCode?: string },
  ): Promise<MfaSetupResponse> {
    return authFetch<MfaSetupResponse>('/auth/mfa/setup', {
      method: 'POST',
      body: {
        currentPassword,
        ...(proof?.totpCode ? { totpCode: proof.totpCode } : {}),
        ...(proof?.recoveryCode ? { recoveryCode: proof.recoveryCode } : {}),
      },
    });
  }

  /**
   * Finish enrollment with the first 6-digit code. Returns the one-time
   * recovery codes. The session SURVIVES enrollment (re-minted with the new
   * `mfa_state_version`), so `reauthRequired` is `false` — do not re-login here,
   * or the freshly-returned codes are lost before the user can save them.
   */
  async mfaVerifySetup(code: string): Promise<MfaVerifySetupResponse> {
    return authFetch<MfaVerifySetupResponse>('/auth/mfa/verify-setup', {
      method: 'POST',
      body: { code },
    });
  }

  /**
   * Cancel an in-progress enrollment: rolls the `enabling` state back to its
   * origin and clears the setup cookie. Best-effort from the client's view — a
   * 409 (nothing in progress / state changed) is fine; the caller refetches status.
   */
  async mfaAbortSetup(): Promise<{ success: boolean }> {
    return authFetch<{ success: boolean }>('/auth/mfa/setup/abort', { method: 'POST' });
  }

  /**
   * Turn MFA off. Requires inline step-up (password + a second factor) — a live
   * session is never proof. The browser session is preserved (re-minted with the
   * new `mfa_state_version`); other devices are NOT force-evicted, so the caller
   * stays signed in and need not re-authenticate.
   */
  async mfaDisable(
    currentPassword: string,
    factor: MfaStepUpFactor,
  ): Promise<{ success: boolean }> {
    return authFetch<{ success: boolean }>('/auth/mfa/disable', {
      method: 'POST',
      body: stepUpBody(currentPassword, factor),
    });
  }

  /**
   * Replace the recovery codes with a fresh (unacknowledged) set. Requires
   * step-up. No factor change → the session is preserved (no sign-out).
   *
   * A backup code is accepted as the factor here and is validated but NOT
   * consumed: this call replaces the whole set moments later, so the presented
   * code is invalidated by the action itself.
   */
  async mfaRegenerateRecoveryCodes(
    currentPassword: string,
    factor: MfaStepUpFactor,
  ): Promise<{ recoveryCodes: string[]; generatedAt?: string }> {
    return authFetch<{ recoveryCodes: string[]; generatedAt?: string }>(
      '/auth/mfa/recovery-codes/regenerate',
      {
        method: 'POST',
        body: stepUpBody(currentPassword, factor),
      },
    );
  }

  /** Mark the current recovery codes as saved ("I saved them"). No step-up. */
  async mfaAcknowledgeRecoveryCodes(): Promise<{ success: boolean }> {
    return authFetch<{ success: boolean }>('/auth/mfa/recovery-codes/acknowledge', {
      method: 'POST',
    });
  }

  /**
   * Self-service reconciliation for a session stuck in a transitional state
   * (`enabling` / `disabling` / `resetting`) or with Cognito drift. May commit
   * a stranded enrollment, in which case `reauthRequired` is set and the caller
   * must re-login.
   */
  async mfaRepair(): Promise<MfaRepairResponse> {
    return authFetch<MfaRepairResponse>('/auth/mfa/repair', { method: 'POST' });
  }

  // -------------------------------------------------------------------------
  // Company portal hand-off (ADR-024 §4.4.1–4.4.2)
  // -------------------------------------------------------------------------

  /** On fias.io: what the Continue screen shows for this authorization request. */
  async getCompanyAuthorize(params: CompanyAuthorizeParams): Promise<CompanyAuthorizeView> {
    const qs = new URLSearchParams({
      host_id: params.hostId,
      client_id: params.clientId,
      redirect_uri: params.redirectUri,
      response_type: params.responseType,
      state: params.state,
      code_challenge: params.codeChallenge,
      code_challenge_method: params.codeChallengeMethod,
    });
    return authFetch<CompanyAuthorizeView>(`/auth/company-authorize?${qs}`);
  }

  /** On fias.io: the explicit "Continue" click. Returns where to send the browser. */
  async continueToCompany(
    params: CompanyAuthorizeParams & { disclosureVersion?: number; registeredHere?: boolean },
  ): Promise<{ redirectUrl: string }> {
    return authFetch<{ redirectUrl: string }>('/auth/company-authorize', {
      method: 'POST',
      body: { ...params },
    });
  }

  async signOut(): Promise<void> {
    try {
      await authFetch('/auth/logout', { method: 'POST' });
    } catch {
      // Logout is best-effort — cookies may be cleared by the server
      // even if the response shape was unexpected.
    }
    setCsrfToken(null);
    markSessionInactive();
  }

  /**
   * Reset client-side session state WITHOUT calling `/auth/logout`. For flows
   * where the backend already revoked the session server-side (MFA enroll /
   * disable / support reset trigger a Cognito global sign-out and clear the
   * auth cookies in their own response) — a `/auth/logout` POST would be a
   * pointless round-trip against already-cleared cookies. Clears the cached
   * CSRF token and the in-memory / durable session hints so the next probe
   * treats the user as logged out.
   */
  clearLocalSession(): void {
    setCsrfToken(null);
    markSessionInactive();
  }

  // -------------------------------------------------------------------------
  // Session lookup
  // -------------------------------------------------------------------------

  async getCurrentUser(): Promise<AuthUser | null> {
    try {
      // A Company portal session mid-refresh on the server answers 503
      // SESSION_REFRESHING — signed in, just not this instant (ADR-024).
      // Wait it out, up to twice, rather than boot as signed out and bounce a
      // signed-in user through a fresh sign-in.
      const result = await authFetch<{ user: AuthUser }>('/auth/me', {
        sessionRefreshingRetries: 2,
      });
      markSessionActive();
      return result.user;
    } catch (err) {
      // The `fias_session` cookie lives 60m (the Cognito ID-token TTL) while
      // `fias_refresh` lives 30 days. The overwhelmingly common case for a 401
      // here is a returning user whose short session cookie lapsed while they
      // were away — overnight, a long meeting, or an iOS tab discard. The
      // refresh token can silently mint a new session. Without this recovery,
      // every cold app load after an hour forces a needless re-login even though
      // the user is still well inside their 30-day window. That was the "we have
      // to log in too often" bug: the proactive timer in AuthContext is gated on
      // `user` already being set, so it never runs on boot, and this is the only
      // session probe at startup. The 401 interceptor in lib/api.ts does not
      // cover it either — that one deliberately skips /auth/* paths.
      //
      // So on a 401, attempt one refresh-and-retry before giving up, and let the
      // SERVER decide whether a session exists. An earlier version gated this on
      // a client-side signal — `fias_session_hint` in localStorage, with
      // `getCsrfToken()` as a fallback — to spare anonymous visitors one
      // round-trip. Both signals are strictly weaker than the credential they
      // guard:
      //
      //   - `getCsrfToken()` is always null on a cold load. The app deploys
      //     cross-origin (frontend `platform.fias.io`, API
      //     `api.platform.fias.io`), so `fias_csrf` is host-only for the API
      //     origin and absent from the frontend's `document.cookie`, and the
      //     in-memory cache is empty until the first login/refresh response.
      //   - localStorage is script-writable storage, which Safari's ITP deletes
      //     after 7 days without a first-party visit — while leaving the
      //     httpOnly `fias_refresh` cookie alone for its full 30 days. On iOS the
      //     gate therefore expires well before the credential it gates, and the
      //     user lands on the login screen holding a valid refresh token.
      //
      // The cost is one extra POST /auth/refresh per cold load for signed-out
      // visitors, which short-circuits to 401 server-side without reaching
      // Cognito. That is the right trade for a gate that cannot drift out of
      // sync with the credential it is gating.
      const status = err instanceof AuthFetchError ? err.status : undefined;

      // Still refreshing after the waiting above: the session is LIVE and the
      // server has said nothing that ends it (only a 401/403 does — the rule
      // `refreshAuthSession` applies on portals). "No user" here would boot
      // the app signed out and show sign-in to a signed-in person, so this is
      // the one probe outcome that is neither a user nor null: the caller
      // (AuthContext) keeps the session and asks again shortly. The durable
      // hint is left alone, like any other transient failure below.
      if (isSessionRefreshingError(err)) {
        throw err;
      }

      if (status === 401) {
        const refreshed = await refreshAuthSession();
        if (refreshed) {
          try {
            const retry = await authFetch<{ user: AuthUser }>('/auth/me');
            markSessionActive();
            return retry.user;
          } catch {
            // Refresh succeeded but /auth/me still failed — fall through to
            // the logged-out path below.
          }
        }
        // Definitive: the refresh cookie is missing, expired, or revoked. Clear
        // the durable hint so the boot/splash heuristics stop treating this
        // browser as a returning session.
        markSessionInactive();
        return null;
      }

      // Anything else — offline, DNS failure, a 5xx, a request that never
      // completed — is TRANSIENT. Report "no user" for this load, but do NOT
      // call markSessionInactive(): it runs clearSessionHint(), and destroying
      // the durable hint on a blip made the logout STICKY. On mobile that fires
      // constantly (Safari restores a backgrounded tab before the radio is up,
      // so the probe rejects with a TypeError carrying no status), and once the
      // hint was gone every later cold boot skipped recovery as well — a
      // permanent logout from one momentary network failure. The refresh cookie
      // may well still be valid, so leave the session state alone and let the
      // next load settle it. `refreshAuthSession` draws the same distinction.
      return null;
    }
  }

  /**
   * Refreshes the session cookie by calling /auth/refresh. The backend
   * uses the (httpOnly) refresh cookie to obtain a new access token and
   * sets a fresh CSRF cookie. Returns true on success, false if the
   * refresh failed and the user must log in again.
   *
   * Delegates to the shared `refreshAuthSession` helper so this call
   * coalesces with any concurrent refreshes triggered by the api.ts
   * 401 interceptor or the proactive AuthContext timer — the backend
   * never sees a thundering herd of refresh requests.
   */
  async refreshSession(): Promise<boolean> {
    return refreshAuthSession();
  }

  // -------------------------------------------------------------------------
  // Password reset / change
  // -------------------------------------------------------------------------

  async forgotPassword(email: string): Promise<void> {
    await authFetch('/auth/forgot-password', {
      method: 'POST',
      body: { email: email.toLowerCase() },
    });
  }

  async confirmForgotPassword(email: string, code: string, newPassword: string): Promise<void> {
    await authFetch('/auth/confirm-forgot-password', {
      method: 'POST',
      body: { email: email.toLowerCase(), code, newPassword },
    });
  }

  /**
   * Change the password for the currently signed-in user. Backend
   * revokes all sessions (calls Cognito GlobalSignOut) and clears
   * cookies, so the caller is logged out on success.
   */
  async changePassword(oldPassword: string, newPassword: string, totpCode?: string): Promise<void> {
    // MFA-enrolled accounts must include a current TOTP code — the backend
    // rejects the change with MFA_CODE_REQUIRED otherwise (step-up: a live
    // session is never proof).
    await authFetch('/auth/change-password', {
      method: 'POST',
      body: { oldPassword, newPassword, ...(totpCode ? { totpCode } : {}) },
    });
    setCsrfToken(null);
  }

  // -------------------------------------------------------------------------
  // Credentials for WebSocket handshakes and plugin iframe bridging
  //
  // Two separate concerns, two separate methods:
  //
  // - `getWebSocketTicket()` mints a single-use, 30s, DynamoDB-backed
  //   ticket via `POST /auth/ws-ticket`. Use as `?ticket=` query param
  //   on the WebSocket URL. Cannot be replayed; cannot be used against
  //   the REST API. This is the secure path — prefer it.
  //
  // - `getPluginBridgeToken(archeId)` mints a capability JWT scoped to
  //   a single arche (audience=plugin-bridge, 5min TTL). The token is
  //   accepted ONLY at /v1/plugins/bridge/:archId and explicitly
  //   rejected at every other authenticated route. A leaked token
  //   grants ≤5min of access to one arche's bridge handler — never
  //   full-account authority. Cached per-arche in memory.
  //
  // The deprecated `getValidIdToken()` / `getValidAccessToken()` are
  // shims preserved for the brief migration window only. They will be
  // removed in commit 2.
  // -------------------------------------------------------------------------

  async getWebSocketTicket(): Promise<string | null> {
    try {
      const result = await authFetch<{ ticket: string }>('/auth/ws-ticket', { method: 'POST' });
      return result.ticket;
    } catch (err) {
      // Only 401 is an auth-related failure that the refresh-and-retry
      // path can recover from. Surface every other status (5xx, 429,
      // network failures) as a connection error so the streaming hook
      // shows CONNECTION_FAILED rather than masquerading the outage as
      // "your session has expired" — we used to swallow all errors here
      // and return null, which the WebSocket layer maps to the auth-
      // failure code path that fires the SessionExpiredListener modal.
      const status = err instanceof AuthFetchError ? err.status : undefined;
      if (status !== 401) throw err;
      const refreshed = await refreshAuthSession();
      if (!refreshed) return null;
      try {
        const result = await authFetch<{ ticket: string }>('/auth/ws-ticket', { method: 'POST' });
        return result.ticket;
      } catch (retryErr) {
        const retryStatus = retryErr instanceof AuthFetchError ? retryErr.status : undefined;
        if (retryStatus !== 401) throw retryErr;
        return null;
      }
    }
  }

  /**
   * Mint (or read from cache) a plugin-bridge capability token scoped
   * to a specific arche. The token is the *only* credential the
   * /v1/plugins/bridge/:archId route accepts; it cannot be used at any
   * other authenticated route.
   *
   * `archeId` is required. Passing a different arche than the one the
   * plugin will call back on will produce a `BRIDGE_TOKEN_ARCHE_MISMATCH`
   * 403 at bridge time.
   *
   * `channel: 'test'` mints the Forge test-channel variant (ADR-023): the
   * token is scoped to the `test_<archeId>` tenant and carries the test
   * build's permission snapshot instead of the live `plugin_config`. It is
   * cached under its own key (`<archeId>:test`) so a production token for
   * the same arche is never handed to a test-channel bridge or vice versa.
   */
  async getPluginBridgeToken(
    archeId: string,
    channel?: PluginBridgeTokenChannel,
  ): Promise<string | null> {
    return this.requestPluginBridgeToken(archeId, channel, { quiet: false });
  }

  /**
   * Start minting an arche's bridge token without waiting for it, so the
   * plugin's first bridge call finds it cached — or joins the mint already
   * running — instead of paying a full round trip after the plugin boots. The
   * plugin renderer calls this as soon as it mounts, while the bundle is still
   * loading. The token stays in the host frame; it is never handed to the
   * plugin. A failure is not logged unless a real call has joined the mint
   * (an arche the user cannot open already shows its own error).
   */
  prefetchPluginBridgeToken(archeId: string, channel?: PluginBridgeTokenChannel): void {
    void this.requestPluginBridgeToken(archeId, channel, { quiet: true });
  }

  private async requestPluginBridgeToken(
    archeId: string,
    channel: PluginBridgeTokenChannel | undefined,
    { quiet }: { quiet: boolean },
  ): Promise<string | null> {
    if (typeof archeId !== 'string' || archeId.length === 0) {
      return null;
    }
    const cacheKey = bridgeTokenCacheKey(archeId, channel);
    const now = Date.now();
    const cached = cachedBridgeTokens.get(cacheKey);
    if (cached && cached.expiresAt > now) {
      return cached.token;
    }
    const inFlight = inFlightBridgeTokens.get(cacheKey);
    if (inFlight) {
      if (!quiet) inFlight.ticket.quiet = false;
      return inFlight.promise;
    }
    const ticket: BridgeTokenMintTicket = { quiet };
    const promise = this.mintPluginBridgeToken(archeId, channel, cacheKey, ticket, now).finally(
      () => {
        if (inFlightBridgeTokens.get(cacheKey)?.ticket === ticket) {
          inFlightBridgeTokens.delete(cacheKey);
        }
      },
    );
    inFlightBridgeTokens.set(cacheKey, { ticket, promise });
    return promise;
  }

  private async mintPluginBridgeToken(
    archeId: string,
    channel: PluginBridgeTokenChannel | undefined,
    cacheKey: string,
    ticket: BridgeTokenMintTicket,
    requestedAt: number,
  ): Promise<string | null> {
    try {
      const query = new URLSearchParams({ archeId });
      if (channel) query.set('channel', channel);
      const result = await authFetch<{ token: string; expiresIn: number }>(
        `/auth/plugin-bridge-token?${query.toString()}`,
      );
      // Dropped while in flight (invalidated, or the session ended — and
      // possibly superseded by a newer mint for the same key): this caller
      // still gets its answer, but it must not land in the cache.
      if (inFlightBridgeTokens.get(cacheKey)?.ticket === ticket) {
        // Server returns expiresIn in seconds. Refresh slightly before
        // the server's expiry so an in-flight bridge call doesn't race.
        const ttlMs = Math.max(0, result.expiresIn * 1000 - PLUGIN_BRIDGE_REFRESH_MARGIN_MS);
        cachedBridgeTokens.set(cacheKey, {
          token: result.token,
          expiresAt: requestedAt + ttlMs,
        });
      }
      return result.token;
    } catch (err) {
      // Surface the REAL failure instead of swallowing it. Returning null
      // makes the caller send an empty token, which the bridge route rejects
      // with the generic "Plugin bridge requires token obtained from
      // /v1/auth/plugin-bridge-token" — masking the actual cause (e.g. a 500
      // from an unconfigured BRIDGE_TOKEN_SECRET, or a 4xx for a
      // non-plugin / unauthorized arche). Log the status/code loudly so the
      // root cause is visible; the null-on-failure contract is preserved.
      if (ticket.quiet) {
        return null;
      }
      if (err instanceof AuthFetchError) {
        console.error(
          `[plugin-bridge-token] mint failed for ${archeId}: HTTP ${err.status}` +
            (err.code ? ` (${err.code})` : '') +
            ` — ${err.message}`,
        );
      } else {
        console.error(`[plugin-bridge-token] mint failed for ${archeId}:`, err);
      }
      return null;
    }
  }

  /**
   * Drop the cached bridge token for an arche. Called by the bridge
   * client when the server returns `BRIDGE_TOKEN_EXPIRED` so the next
   * `getPluginBridgeToken(archeId)` mints a fresh one.
   */
  invalidatePluginBridgeToken(archeId: string, channel?: PluginBridgeTokenChannel): void {
    const cacheKey = bridgeTokenCacheKey(archeId, channel);
    cachedBridgeTokens.delete(cacheKey);
    // A mint already running is dropped too, so "the next call mints fresh"
    // holds even mid-flight; its own caller still gets its answer.
    inFlightBridgeTokens.delete(cacheKey);
  }

  /**
   * Synchronous "do we have a session?" probe. Returns a stable
   * non-null marker string when the user is logged in, or null
   * otherwise. Used by streaming/WebSocket hooks as a render-time
   * gate; they call `getWebSocketTicket()` async to obtain the actual
   * ticket used in the WS handshake. The marker is never sent on the
   * wire — it's purely a truthy/falsy signal.
   */
  getIdToken(): string | null {
    return sessionPresent ? SESSION_PRESENT_MARKER : null;
  }

  getAccessToken(): string | null {
    return this.getIdToken();
  }

  // -------------------------------------------------------------------------
  // Dev mode helpers
  // -------------------------------------------------------------------------

  isDevMode(): boolean {
    return DEV_MODE;
  }

  getDevUsers(): AuthUser[] {
    return DEV_USERS;
  }

  /**
   * Switches to a different dev user. Re-runs /auth/dev-login so the
   * server-set cookies are updated to match the new dev user.
   */
  async switchDevUser(index: number): Promise<AuthUser | null> {
    if (!DEV_MODE) return null;
    if (index < 0 || index >= DEV_USERS.length) return null;
    setSelectedDevUserIndex(index);
    try {
      await authFetch('/auth/dev-login', {
        method: 'POST',
        body: { devUserIndex: index + 1 },
      });
      markSessionActive();
    } catch (err) {
      console.warn('[DEV_MODE] dev-login failed', err);
      return null;
    }
    return DEV_USERS[index];
  }
}

/** An OAuth authorization request for a Company portal's first-party client. */
export interface CompanyAuthorizeParams {
  /**
   * The portal the Continue page is branded as (its URL). The API refuses
   * unless it is the portal `clientId` belongs to.
   */
  hostId: string;
  clientId: string;
  redirectUri: string;
  responseType: string;
  state: string;
  codeChallenge: string;
  codeChallengeMethod: string;
}

export interface CompanyAuthorizeView {
  portal: {
    hostId: string;
    hostname: string;
    displayLabel: string | null;
    companyName: string | null;
    brand: CompanyPortalBrand | null;
  };
  needsDisclosure: boolean;
  disclosureVersion: number;
}

/** Brand tokens a Company portal and its branded sign-in show. */
export interface CompanyPortalBrand {
  displayName: string | null;
  logoDataUrl: string | null;
  primaryColor: string | null;
  accentColor: string | null;
  headerTextColor: string | null;
}

export const authApi = new AuthApiClient();
export default authApi;
